import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { nlpEngine } from '@/utils/nlp';
import { useLanguage } from '@/contexts/LanguageContext';
// import { useTranslation } from 'react-i18next'; // Not used currently

interface Message {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const Chatbot: React.FC = () => {
  const location = useLocation();
  const { language } = useLanguage();
  // const { t } = useTranslation(); // Not used currently
  
  // İlk karşılama mesajını dile göre ayarla
  const getInitialMessage = () => {
    if (language === 'en') {
      return "Hello! Welcome. I'm Sezar, an AI assistant developed by CRV Software with NLP. How can I help you?";
    }
    return "Merhaba! Hoş geldiniz. Ben Sezar, CRV Software tarafından NLP ile geliştirilmiş yapay zeka asistanınızım. Size nasıl yardımcı olabilirim?";
  };
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      text: getInitialMessage(),
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Animasyon state'leri
  const [showGreeting, setShowGreeting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Dil değiştiğinde chatbot'u sıfırla
  useEffect(() => {
    setMessages([
      {
        id: 1,
        text: getInitialMessage(),
        isUser: false,
        timestamp: new Date()
      }
    ]);
    setInputText('');
  }, [language]);

  // Gelişmiş NLP tabanlı chatbot mantığı
  const getBotResponse = (userMessage: string): string => {
    // NLP analizi yap
    const analysis = nlpEngine.analyzeMessage(userMessage);
    const intent = analysis.intent;
    // const confidence = analysis.confidence; // Not used currently
    
    // Dil kontrolü
    const isEnglish = language === 'en';
    
    // NLP tabanlı intent yönlendirmesi
    if (intent === 'education') {
      return isEnglish 
        ? `Yes, absolutely! 😊

Thanks to our experienced instructors, we can provide training according to your level. Frontend training with HTML, CSS, TailwindCSS, JS and React, backend training with Python (Django), or database training.

You can choose one-on-one private lessons, group training or online programs that suit you. We also combine theoretical knowledge with practice through our project-based learning approach.

All you have to do is contact us through our communication channels for more detailed information. Let's determine the most suitable training program for you together!`
        : `Evet, kesinlikle öğrenebilirsiniz! 😊

Tecrübeli eğitmenlerimiz sayesinde seviyenize göre eğitim verebiliriz. HMTL,CSS,TailwindCSS,JS ve React ile frontend eğitimi ,en popüler backend dili olan Python(Django)'la backend eğitimi veya veritabanı eğitimi yapabiliriz.

Birebir özel dersler, grup eğitimleri veya online programlarımızdan size uygun olanını seçebilirsiniz. Ayrıca proje bazlı öğrenme yaklaşımımızla teorik bilgiyi pratikle harmanlıyoruz.

Daha detaylı bilgi için iletişim kanallarımızdan bize ulaşmanız yeterli. Size en uygun eğitim programını birlikte belirleyelim!`;
    }
    
    if (intent === 'services') {
      return isEnglish
        ? `Of course! We can offer you the most suitable solution.

Do you need a website? We develop fast and secure web applications with modern technologies. We can do everything from e-commerce sites to corporate web solutions with current frameworks like React, Vue.js, Angular.

Do you want a mobile app? We offer native applications for iOS and Android or cross-platform solutions with React Native. We handle everything up to the App Store.

Do you need a desktop program? We develop custom desktop applications with Python.

We also develop your Artificial Intelligence Applications with our professional team.

What do you need help with? You can reach us at crvsoftwareteam@gmail.com for detailed information. Let's find a custom solution for you!`
        : `Tabii ki! Size en uygun çözümü sunabiliriz. 

Web siteniz mi lazım? Modern teknolojilerle hızlı ve güvenli web uygulamaları geliştiriyoruz. React, Vue.js, Angular gibi güncel framework'lerle e-ticaret sitelerinden kurumsal web çözümlerine kadar her şeyi yapabiliriz.

Mobil uygulama mı istiyorsunuz? iOS ve Android için native uygulamalar veya React Native ile cross-platform çözümler sunuyoruz. App Store'a kadar her şeyi hallediyoruz.

Masaüstü programı mı gerekli? Python ile ihtiyacınıza özel masaüstü uygulamaları geliştiriyoruz.

Yapay Zeka Uygulamalarınızı da profesyonel ekibimizle geliştiriyoruz.

Hangi konuda yardıma ihtiyacınız var? Detaylı bilgi için crvsoftwareteam@gmail.com adresinden bize ulaşabilirsiniz. Size özel çözümü birlikte bulalım!`;
    }
    
    if (intent === 'contact') {
      return isEnglish
        ? `Of course! You can reach us easily. 📞

Email: crvsoftwareteam@gmail.com
Phone: +90 (532) 471 75 22
Website: https://crvsoftware.com
Location: Niğde, Turkey
WhatsApp: Available

Our working hours are Monday-Friday 09:00-18:00. But you can also reach us via WhatsApp for emergencies.

Whichever way you prefer, we'll get back to you at the most convenient time. We're excited to talk about your project! 😊

Get in touch right away, let's offer you custom solutions!`
        : `Tabii ki! Bize en kolay şekilde ulaşabilirsiniz. 📞

E-posta: crvsoftwareteam@gmail.com
Telefon: +90 (532) 471 75 22
Website: https://crvsoftware.com
Lokasyon: Niğde, Türkiye
WhatsApp: Mevcut

Çalışma saatlerimiz Pazartesi-Cuma 09:00-18:00 arası. Ama acil durumlar için WhatsApp'tan da ulaşabilirsiniz.

Hangi yolu tercih ederseniz edin, size en uygun zamanda dönüş yaparız. Projeniz hakkında konuşmak için sabırsızlanıyoruz! 😊

Hemen iletişime geçin, size özel çözümler sunalım!`;
    }
    
    if (intent === 'pricing') {
      return `Merak etmeyin, size en uygun fiyatı sunacağız! 

Öncelikle ücretsiz danışmanlık hizmeti veriyoruz. Projenizi analiz edip, en uygun teknolojiyi seçip, detaylı fiyat teklifi hazırlıyoruz. Hiçbir yükümlülük olmadan konuşabiliriz.

Fiyatlandırma tamamen projenizin karmaşıklığına, özellik sayısına ve süresine göre belirleniyor. Şeffaflık bizim için çok önemli, gizli maliyet yok.

Ayrıca ilk 30 gün ücretsiz destek, kaynak kod teslimi ve detaylı dokümantasyon da dahil. Yani projeniz bitince yalnız kalmıyorsunuz.

Fiyat teklifi almak için crvsoftwareteam@gmail.com adresine proje detaylarınızı gönderin. Size özel teklifi hazırlayalım! `;
    }
    
    if (intent === 'projects') {
      return `Tabii ki! Projelerimizi inceleyebilirsiniz. 

150'den fazla başarılı projemiz var! E-ticaret platformlarından kurumsal web uygulamalarına, mobil uygulamalardan masaüstü yazılımlara kadar geniş bir yelpazede çalışmalarımız mevcut.

%100 müşteri memnuniyeti oranımızla gurur duyuyoruz. 100'den fazla mutlu müşterimiz var ve 5+ yıllık deneyimimizle modern teknolojileri kullanarak projeler geliştiriyoruz.

Detaylı portfolyomuzu görmek için https://crvsoftware.com/projeler adresini ziyaret edebilirsiniz. Projelerimizi inceleyin, size nasıl yardımcı olabileceğimizi görün! 

Belki sizin projeniz de bu listeye eklenir! 😊`;
    }
    
    if (intent === 'about') {
      return `Çok güzel bir soru! 😊

CRV Software olarak misyonumuz teknolojiyi geleceğe taşımak. Uzman geliştirici kadromuzla 5+ yıllık sektör tecrübemizi birleştirerek modern çözümler üretiyoruz.

Frontend'te React; backend'te Python,  mobilde React Native, Flutter; veritabanında PostgreSQL, MongoDB, MySQL; cloud'da AWS, Azure, Google Cloud gibi güncel teknolojileri kullanıyoruz.

Müşteri odaklı yaklaşımımız, kaliteli ve zamanında teslimatımız, şeffaf iletişimimiz ve sürekli gelişim anlayışımızla fark yaratıyoruz.

Detaylı bilgi için https://crvsoftware.com/hakkimizda adresini ziyaret edebilirsiniz. Teknoloji dünyasında fark yaratmaya devam ediyoruz! `;
    }
    
    if (intent === 'technology') {
      return `Harika soru! Teknoloji stack'imiz oldukça geniş. 

Frontend'te React, Vue.js, Angular gibi modern framework'ler kullanıyoruz. TypeScript, JavaScript, Tailwind CSS, Bootstrap ile güzel arayüzler tasarlıyoruz. Next.js, Nuxt.js ile de performanslı uygulamalar geliştiriyoruz.

Backend'te Python (Django, Flask), Node.js (Express), PHP (Laravel), C#, Java gibi dilleri tercih ediyoruz. Her projenin ihtiyacına göre en uygun teknolojiyi seçiyoruz.

Mobil tarafında React Native, Flutter ile cross-platform uygulamalar yapıyoruz. iOS için Swift, Android için Kotlin ile native çözümler de sunuyoruz.

Veritabanı olarak PostgreSQL, MySQL, MongoDB, Redis, SQLite kullanıyoruz. Cloud'da AWS, Azure, Google Cloud ile çalışıyoruz. Docker, Kubernetes, CI/CD Pipeline ile DevOps süreçlerini yönetiyoruz.

Hangi teknoloji hakkında daha detaylı bilgi almak istersiniz? Size özel örnekler verebilirim! 😊`;
    }
    
    if (intent === 'process') {
      return `Çok güzel bir soru! Sürecimiz çok düzenli ve şeffaf. 

İlk olarak ihtiyaçlarınızı analiz edip, teknik değerlendirme yapıyoruz. Proje planlaması ve zaman çizelgesi oluşturuyoruz. Size net bir roadmap veriyoruz.

Sonra UI/UX tasarım aşamasına geçiyoruz. Mockup'lar oluşturup, kullanıcı deneyimini optimize ediyoruz. Sizin onayınızla devam ediyoruz.

Geliştirme aşamasında Agile metodoloji kullanıyoruz. Düzenli güncellemeler veriyoruz, kod kalitesini kontrol ediyoruz. Test süreçlerini de ihmal etmiyoruz.

Test & optimizasyon aşamasında fonksiyonel testler yapıyoruz, performansı optimize ediyoruz, güvenlik kontrollerini yapıyoruz.

Son olarak canlıya alıyoruz, eğitim veriyoruz, dokümantasyon hazırlıyoruz. 30 gün ücretsiz destek ve bakım hizmeti sunuyoruz.

Detaylı bilgi için iletişime geçin! Size özel süreç planı hazırlayalım! `;
    }
    
    // Karşılama mesajları
    if (intent === 'greeting' || userMessage.toLowerCase().includes('merhaba') || userMessage.toLowerCase().includes('selam') || 
        userMessage.toLowerCase().includes('hello') || userMessage.toLowerCase().includes('hi')) {
      return isEnglish
        ? `Hello! Welcome to CRV Software! 😊

I'm Sezar, your digital assistant and I'm here to help you. What would you like to know about?

We can talk about our web, mobile, desktop development services. You can learn our contact information, get a quote, check out our projects.

I can also provide information about our technology stack, working process, training services. Just ask, let me give you the most appropriate answer!`
        : `Merhaba! CRV Software'e hoş geldiniz! 😊

Ben Sezar, sizin dijital asistanınızım ve size yardımcı olmak için buradayım. Hangi konuda bilgi almak istersiniz?

Web, mobil, masaüstü geliştirme hizmetlerimiz hakkında konuşabiliriz. İletişim bilgilerimizi öğrenebilir, fiyat teklifi alabilir, projelerimizi inceleyebilirsiniz.

Teknoloji stack'imiz, çalışma sürecimiz, eğitim hizmetlerimiz hakkında da bilgi verebilirim. Sadece sorun, size en uygun cevabı vereyim!`;
    }
    
    // Teşekkür mesajları
    if (userMessage.toLowerCase().includes('teşekkür') || userMessage.toLowerCase().includes('sağol') ||
        userMessage.toLowerCase().includes('thank') || userMessage.toLowerCase().includes('thanks')) {
      return isEnglish
        ? `You're welcome! 😊

I'm Sezar, and I'm glad I could help!

If you have any other questions, feel free to ask. As CRV Software, we are always with you!

Don't forget to get in touch for successful projects. We're waiting for you!`
        : `Rica ederim! 😊

Ben Sezar, size yardımcı olabildiysem ne mutlu bana! 

Başka sorularınız varsa çekinmeden sorabilirsiniz. CRV Software olarak her zaman yanınızdayız! 

Başarılı projeler için iletişime geçmeyi unutmayın. Sizi bekliyoruz!`;
    }
    
    // Ek kategoriler - çok daha kapsamlı
    
    if (intent === 'ecommerce') {
      return `E-ticaret Çözümlerimiz:

**E-ticaret Platformları**
• Özel e-ticaret siteleri
• Mevcut platform entegrasyonları
• Çok dilli ve çok para birimli sistemler
• SEO optimizasyonu

**Özellikler**
• Ürün kataloğu yönetimi
• Sepet ve ödeme sistemleri
• Stok takibi
• Müşteri yönetimi
• Raporlama ve analitik

**Entegrasyonlar**
• Ödeme sistemleri (PayPal, Stripe, İyzico)
• Kargo firmaları
• Muhasebe sistemleri
• CRM entegrasyonu

Detaylı bilgi için iletişime geçin!`;
    }
    
    if (intent === 'seo') {
      return `SEO ve Dijital Pazarlama:

**SEO Hizmetleri**
• Anahtar kelime araştırması
• On-page SEO optimizasyonu
• Teknik SEO analizi
• İçerik optimizasyonu
• Link building stratejileri

**Dijital Pazarlama**
• Google Ads yönetimi
• Sosyal medya pazarlama
• E-posta pazarlama kampanyaları
• İçerik pazarlama
• Analitik ve raporlama

**Sosyal Medya**
• Profil optimizasyonu
• İçerik stratejisi
• Reklam yönetimi
• Topluluk yönetimi

Hedef kitlenize ulaşın, satışlarınızı artırın!`;
    }
    
    if (intent === 'security') {
      return `Güvenlik Çözümlerimiz:

**Web Güvenliği**
• SSL sertifikası kurulumu
• Güvenlik duvarı yapılandırması
• SQL injection koruması
• XSS saldırı önleme
• DDoS koruması

**Veri Güvenliği**
• Veritabanı şifreleme
• Backup ve recovery sistemleri
• KVKK uyumlu veri işleme
• Kullanıcı yetkilendirme
• Log yönetimi

**Sürekli İzleme**
• 7/24 güvenlik izleme
• Anlık uyarı sistemleri
• Güvenlik güncellemeleri
• Penetrasyon testleri

Verilerinizi güvende tutuyoruz!`;
    }
    
    if (intent === 'maintenance') {
      return `Bakım ve Destek Hizmetleri:

**Teknik Destek**
• 7/24 teknik destek
• Uzaktan erişim desteği
• Acil müdahale
• Sistem izleme

**Bakım Hizmetleri**
• Düzenli güncellemeler
• Performans optimizasyonu
• Güvenlik yamaları
• Backup yönetimi

**Hosting ve Domain**
• Güvenilir hosting çözümleri
• Domain yönetimi
• E-posta kurulumu
• SSL sertifikası

**Eğitim**
• Kullanıcı eğitimleri
• Dokümantasyon
• Video eğitimler
• Canlı destek

Projeleriniz güvende!`;
    }
    
    if (intent === 'mobile') {
      return `Mobil Uygulama Geliştirme:

**Platform Desteği**
• iOS uygulamaları (Swift, Objective-C)
• Android uygulamaları (Kotlin, Java)
• Cross-platform çözümler (React Native, Flutter)
• PWA (Progressive Web App) geliştirme

**Özellikler**
• Push notification sistemi
• Offline çalışma desteği
• Sosyal medya entegrasyonu
• Ödeme sistemi entegrasyonu
• GPS ve harita entegrasyonu

**App Store Optimizasyonu**
• Store listing optimizasyonu
• ASO (App Store Optimization)
• İcon ve screenshot tasarımı
• Açıklama ve keyword optimizasyonu

**Yayınlama Süreci**
• App Store ve Google Play yayınlama
• Store onay süreçleri
• Güncelleme yönetimi
• Kullanıcı geri bildirim yönetimi

Mobil dünyada başarıya ulaşın!`;
    }
    
    if (intent === 'design') {
      return `Web Tasarım ve Görsel Kimlik:

**UI/UX Tasarım**
• Kullanıcı deneyimi tasarımı
• Arayüz tasarımı (UI)
• Wireframe ve prototip oluşturma
• Kullanıcı testleri ve optimizasyon

**Responsive Tasarım**
• Mobil uyumlu tasarım
• Tablet ve desktop optimizasyonu
• Cross-browser uyumluluk
• Hızlı yükleme süreleri

**Görsel Kimlik**
• Logo tasarımı
• Kurumsal kimlik çalışmaları
• Renk paleti ve tipografi
• Marka rehberi oluşturma

**Modern Tasarım Trendleri**
• Minimalist tasarım
• Micro-interactions
• Gradient ve animasyonlar
• Dark/Light mode desteği

Markanızı öne çıkarın!`;
    }
    
    if (intent === 'database') {
      return `Veritabanı ve API Geliştirme:

**Veritabanı Çözümleri**
• MySQL, PostgreSQL optimizasyonu
• MongoDB NoSQL veritabanları
• Redis cache sistemleri
• Veritabanı tasarımı ve optimizasyonu

**API Geliştirme**
• RESTful API tasarımı
• GraphQL API geliştirme
• API dokümantasyonu
• API güvenlik ve doğrulama

**Entegrasyonlar**
• Üçüncü parti API entegrasyonları
• Webhook yapılandırması
• Veri senkronizasyonu
• Real-time veri akışı

**Performans**
• Veritabanı optimizasyonu
• Query optimizasyonu
• Indexing stratejileri
• Caching mekanizmaları

Güçlü backend altyapısı!`;
    }
    
    if (intent === 'cloud') {
      return `Bulut ve DevOps Çözümleri:

**Bulut Platformları**
• AWS (Amazon Web Services)
• Microsoft Azure
• Google Cloud Platform
• Bulut mimarisi tasarımı

**Konteyner Teknolojileri**
• Docker konteynerizasyonu
• Kubernetes orchestration
• Mikroservis mimarisi
• Service mesh çözümleri

**CI/CD Pipeline**
• Otomatik deployment
• Test otomasyonu
• Code quality kontrolleri
• Continuous integration

**Monitoring ve Logging**
• Sistem izleme
• Log yönetimi
• Performance monitoring
• Alert sistemleri

Modern DevOps yaklaşımı!`;
    }
    
    
    if (intent === 'payment') {
      return `Ödeme ve Faturalama:

**Ödeme Seçenekleri**
• Banka havalesi
• Kredi kartı ile ödeme
• Taksitli ödeme seçenekleri
• Dijital cüzdan ödemeleri

**Faturalama**
• E-fatura sistemi
• KDV dahil fiyatlandırma
• Detaylı proje faturaları
• Ödeme takip sistemi

**Proje Fiyatlandırması**
• Sabit fiyat teklifleri
• Saatlik çalışma seçenekleri
• Paket fiyat seçenekleri
• Özel indirim kampanyaları

**Garanti ve Destek**
• 30 gün ücretsiz destek
• Kaynak kod teslimi
• Dokümantasyon
• Eğitim ve kurulum

Şeffaf fiyatlandırma, güvenli ödeme!`;
    }
    
    // Özel durumlar için daha spesifik kontroller
    
    // Sadece "veriyor" ile biten sorular için
    // Genel hizmet sorusu
    if (userMessage.toLowerCase().includes('veriyor') || userMessage.toLowerCase().includes('yapıyor')) {
      return `Evet, CRV Software olarak şu hizmetleri sunuyoruz:

**Yazılım Geliştirme Hizmetleri**
• Web uygulamaları (React)
• Mobil uygulamalar (iOS, Android, React Native)
• Masaüstü uygulamalar (Python)
• E-ticaret çözümleri

**Dijital Pazarlama**
• SEO optimizasyonu
• Google Ads yönetimi
• Sosyal medya pazarlama

**Danışmanlık ve Eğitim**
• Yazılım eğitimleri
• Proje danışmanlığı
• Teknoloji seçimi

**Destek Hizmetleri**
• 7/24 teknik destek
• Hosting ve domain
• Güvenlik çözümleri

Detaylı bilgi için iletişime geçin!`;
    }
    
    // Varsayılan yanıt - NLP tabanlı
    return `Anladım! Size daha iyi yardımcı olabilmem için şu konulardan birini sorabilirsiniz:

Hizmetlerimiz neler? İletişim bilgileriniz? Fiyat bilgisi alabilir miyim? Projelerinizi görebilir miyim? Hakkımızda bilgi alabilir miyim?

Hangi teknolojileri kullanıyorsunuz? Proje süreciniz nasıl işliyor? E-ticaret sitesi yapıyor musunuz? SEO hizmeti veriyor musunuz?

Mobil uygulama geliştiriyor musunuz? Web tasarım yapıyor musunuz? Veritabanı ve API geliştirme? Bulut ve DevOps çözümleri?

Eğitim ve danışmanlık? Güvenlik çözümleri? Bakım ve destek hizmetleri?

Ya da doğrudan sorunuzu yazabilirsiniz. Size en uygun çözümü bulalım! 😊`;
  };

  const sendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now(),
      text: inputText,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Bot yanıtını simüle et
    setTimeout(() => {
      const botResponse: Message = {
        id: Date.now() + 1,
        text: getBotResponse(inputText),
        isUser: false,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  // Auto scroll to bottom when new messages arrive
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Sayfa değişikliğinde animasyon tetikle
  useEffect(() => {
    setIsAnimating(true);
    setShowGreeting(true);
    
    const timer = setTimeout(() => {
      setShowGreeting(false);
      setIsAnimating(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button - Space Theme */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 hover:from-gray-800 hover:via-blue-800 hover:to-purple-800 text-white shadow-2xl shadow-cyan-500/30 transition-all duration-500 hover:scale-110 flex items-center gap-3 border-2 border-cyan-500/50 ${
            showGreeting 
              ? 'px-6 py-3 rounded-full' 
              : 'p-1 rounded-full'
          } ${
            isAnimating 
              ? 'animate-slide-in-left' 
              : ''
          }`}
          style={{
            animation: isAnimating ? 'slideInLeft 0.5s ease-out' : 'none'
          }}
        >
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-full blur-xl"></div>
          
          {isOpen ? (
            <X size={24} className="relative z-10" />
          ) : (
            <>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-md animate-pulse"></div>
                <img 
                  src="/assistant-icon.ico" 
                  alt="Assistant" 
                  className="relative w-14 h-14 border-2 border-cyan-400 rounded-full shadow-lg shadow-cyan-500/50"
                />
              </div>
              {showGreeting && (
                <span className="relative font-bold text-sm whitespace-nowrap animate-fade-in text-cyan-300">
                  {language === 'en' ? 'Hello' : 'Merhaba'}
                </span>
              )}
            </>
          )}
        </button>
      </div>
      
      <style>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.5s ease-out;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in 0.2s both;
        }
      `}</style>

      {/* Chatbot Window - Space Theme */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-80 sm:w-96 h-[32rem] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-cyan-500/30 backdrop-blur-xl">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/50 to-purple-900/50 opacity-95"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDAsIDI1NSwgMjU1LCAwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
          
          {/* Stars */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`
                }}
              />
            ))}
          </div>

          {/* Header */}
          <div className="relative bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 backdrop-blur-md border-b border-cyan-500/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-md animate-pulse"></div>
                  <img 
                    src="/assistant-icon.ico" 
                    alt="Assistant" 
                    className="relative w-12 h-12 rounded-full border-2 border-cyan-400 shadow-lg shadow-cyan-500/50"
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-gray-900 animate-pulse"></div>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg tracking-wide">CRV Software</h3>
                  <p className="text-xs text-cyan-300 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    {language === 'en' ? 'Sezar Active' : 'Sezar Aktif'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-cyan-300 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="relative flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(34, 211, 238, 0.3) transparent'
          }}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
                style={{
                  animationDelay: `${index * 0.05}s`
                }}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm backdrop-blur-md ${
                    message.isUser
                      ? 'bg-gradient-to-r from-cyan-500/80 to-blue-500/80 text-white border border-cyan-400/50 shadow-lg shadow-cyan-500/30'
                      : 'bg-gray-800/60 text-gray-100 border border-gray-700/50 shadow-lg'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">{message.text}</div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-gray-800/60 text-gray-100 px-4 py-3 rounded-2xl border border-gray-700/50 backdrop-blur-md shadow-lg">
                  <div className="flex space-x-2">
                    <div className="w-2.5 h-2.5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-bounce shadow-lg shadow-cyan-500/50"></div>
                    <div className="w-2.5 h-2.5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-bounce shadow-lg shadow-cyan-500/50" style={{animationDelay: '0.15s'}}></div>
                    <div className="w-2.5 h-2.5 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-bounce shadow-lg shadow-cyan-500/50" style={{animationDelay: '0.3s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="relative p-4 border-t border-cyan-500/30 bg-gray-900/50 backdrop-blur-md">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={language === 'en' ? 'Type your message...' : 'Mesajınızı yazın...'}
                className="flex-1 px-4 py-3 bg-gray-800/50 border border-cyan-500/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm text-white placeholder-gray-400 backdrop-blur-sm transition-all"
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim()}
                className="relative bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:from-gray-600 disabled:to-gray-700 text-white p-3 rounded-xl transition-all duration-300 shadow-lg disabled:shadow-none hover:shadow-cyan-500/50 disabled:cursor-not-allowed group"
              >
                <Send size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                {!inputText.trim() && (
                  <div className="absolute inset-0 bg-gray-900/50 rounded-xl"></div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
